/**
 * prefetchData — Pre-carga datos de módulos en background
 *
 * Estrategia:
 *  - Se llama desde Home.js después de que carga la página (via requestIdleCallback)
 *  - Descarga bundles JS de páginas lazy + pre-llena el pageCache con datos del servidor
 *  - Cuando el usuario navega, los datos ya están listos → CERO spinner en segunda visita
 */

import axios from 'axios';
import { setPageCache, getPageCache } from './pageCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const _inFlight = new Set();

async function _prefetch(key, fetcher, ttlMs) {
  if (getPageCache(key) || _inFlight.has(key)) return;
  _inFlight.add(key);
  try {
    const data = await fetcher();
    if (data) setPageCache(key, data, ttlMs);
  } catch {
    // Silencioso — prefetch es best-effort
  } finally {
    _inFlight.delete(key);
  }
}

export function prefetchVentas(empresaId) {
  _prefetch(`page_ventas_${empresaId}`, async () => {
    const [ventasRes, clientesRes, productosRes, galponesRes] = await Promise.all([
      axios.get(`${API}/ventas/multi-moneda/lista?empresa_id=${empresaId}&skip=0&limit=100`).catch(() => ({ data: {} })),
      axios.get(`${API}/clientes/selector?empresa_id=${empresaId}`).catch(() => ({ data: [] })),
      axios.get(`${API}/productos?empresa_id=${empresaId}`).catch(() => ({ data: [] })),
      axios.get(`${API}/galpones?empresa_id=${empresaId}`).catch(() => ({ data: [] })),
    ]);
    const cData = clientesRes.data;
    return {
      ventas: ventasRes.data.ventas || [],
      ventasTotal: ventasRes.data.total || 0,
      ventasResumen: ventasRes.data.resumen || null,
      clientes: Array.isArray(cData) ? cData : (cData?.clientes || []),
      productos: Array.isArray(productosRes.data) ? productosRes.data : [],
      galpones: Array.isArray(galponesRes.data) ? galponesRes.data : [],
    };
  }, 30000);
}

export function prefetchGalpones(empresaId) {
  _prefetch(`page_galpones_${empresaId}`, async () => {
    const [galponesRes, productosRes, stocksRes, movimientosRes, clientesRes, resumenRes] = await Promise.all([
      axios.get(`${API}/galpones?empresa_id=${empresaId}`).catch(() => ({ data: [] })),
      axios.get(`${API}/galpones/productos?empresa_id=${empresaId}`).catch(() => ({ data: [] })),
      axios.get(`${API}/galpones/stock?empresa_id=${empresaId}`).catch(() => ({ data: [] })),
      axios.get(`${API}/galpones/movimientos?empresa_id=${empresaId}&limit=50`).catch(() => ({ data: [] })),
      axios.get(`${API}/clientes/selector?empresa_id=${empresaId}`).catch(() => ({ data: [] })),
      axios.get(`${API}/galpones/stock/resumen?empresa_id=${empresaId}`).catch(() => ({ data: null })),
    ]);
    const cData = clientesRes.data;
    return {
      galpones: Array.isArray(galponesRes.data) ? galponesRes.data : [],
      productos: Array.isArray(productosRes.data) ? productosRes.data : [],
      stocks: Array.isArray(stocksRes.data) ? stocksRes.data : [],
      movimientos: Array.isArray(movimientosRes.data) ? movimientosRes.data : [],
      clientes: Array.isArray(cData) ? cData : (cData?.clientes || []),
      resumen: resumenRes.data || null,
    };
  }, 30000);
}

export function prefetchClientes(empresaId) {
  _prefetch(`page_clientes_${empresaId}`, async () => {
    const res = await axios.get(`${API}/clientes/con-ventas?empresa_id=${empresaId}`).catch(() => ({ data: {} }));
    const cData = res.data;
    return { clientes: cData.clientes || (Array.isArray(cData) ? cData : []) };
  }, 60000);
}

export function prefetchBancoEco(empresaId) {
  _prefetch(`page_banco_${empresaId}`, async () => {
    const res = await axios.get(`${API}/finanzas/banco-eco/${empresaId}?skip=0&limit=100`).catch(() => ({ data: {} }));
    if (!res.data || (res.data.movimientos === undefined && res.data.total_movimientos === undefined)) return null;
    return {
      data: res.data,
      bancoTotal: res.data.total_movimientos || 0,
    };
  }, 30000);
}

export function prefetchCajaChica(empresaId) {
  _prefetch(`page_caja_${empresaId}`, async () => {
    const res = await axios.get(`${API}/finanzas/caja-chica/${empresaId}?skip=0&limit=100`).catch(() => ({ data: {} }));
    if (!res.data || res.data.saldo_actual === undefined) return null;
    return { data: res.data };
  }, 30000);
}

/**
 * Pre-descarga los bundles JS de las páginas más usadas.
 * Elimina el delay de React.lazy en la primera visita a cada módulo.
 */
export function prefetchBundles() {
  try {
    import('../pages/Dashboard');
    import('../pages/Ventas');
    import('../pages/Galpones');
    import('../pages/CajaChica');
    import('../pages/Clientes');
    import('../pages/BancoEco');
    import('../pages/RRHHCompleto');
    import('../pages/Compras');
    import('../pages/Logistica');
    import('../pages/FinanzasEco');
    import('../pages/ReporteDiarioEco');
  } catch {
    // Silencioso
  }
}

/**
 * Pre-carga datos y bundles de todos los módulos de una empresa.
 * Llamar desde Home.js con requestIdleCallback.
 */
export function prefetchAllForEmpresa(empresaId) {
  prefetchVentas(empresaId);
  prefetchGalpones(empresaId);
  prefetchClientes(empresaId);
  prefetchBancoEco(empresaId);
  prefetchCajaChica(empresaId);
}
